import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import 'katex/dist/katex.min.css';
import { evaluateComponentModule, scrubHtmlCommentTokens } from '../componentSandbox';
import { hide, reveal } from './domVisibility';

/**
 * Renders a premium slot component from its raw JSX SOURCE, transpiling in the
 * browser with @babel/standalone.
 *
 * This is now the ADMIN AUTHORING path only — previews, and the transitional
 * fallback for a record whose server-side compile has not run yet. Public pages
 * use PrecompiledComponentRenderer, which needs no Babel.
 *
 * Babel is ~2.9 MB and is therefore loaded through a dynamic import() so
 * webpack emits it as an async chunk. A static import would put it back in the
 * single `application` entrypoint that every page script-tags, because
 * packs/application.js bundles this directory by require.context path match
 * rather than by import — so removing the import from the entrypoint's reach is
 * not an option, only deferring the load is.
 */
type DynamicComponentRendererProps = {
  componentCode: string;
  props: Record<string, unknown>;
  allowedModules?: string[];
  fallbackSelector?: string;
  placeholderSelector?: string;
};

const DynamicComponentRenderer = ({
  componentCode,
  props,
  allowedModules,
  fallbackSelector,
  placeholderSelector,
}: DynamicComponentRendererProps) => {
  const rootRef = useRef<Root | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const babelModule = await import(/* webpackChunkName: "babel-standalone" */ '@babel/standalone');
        // @babel/standalone ships a UMD bundle, so webpack cannot detect named
        // exports on it and exposes the whole thing as the namespace's default.
        // Under babel-jest's CJS interop `default` is that same object, so this
        // works in both. Do not "simplify" to `babelModule.transform`.
        const Babel = ((babelModule as unknown) as { default?: typeof babelModule }).default || babelModule;

        const transpiled = Babel.transform(componentCode, {
          presets: [
            ['env', { modules: 'commonjs' }],
            'react',
          ],
        }).code as string;

        const DynamicComponent = evaluateComponentModule(
          scrubHtmlCommentTokens(transpiled),
          allowedModules,
        );

        if (cancelled) return;

        if (!rootRef.current && containerRef.current) {
          rootRef.current = createRoot(containerRef.current);
        }

        if (rootRef.current) {
          const root = rootRef.current;
          // Commit the user component synchronously so it is in the DOM before we
          // hide the skeleton — avoids a skeleton→blank→content stutter. Safe here:
          // this runs in an effect (post-commit), not during render.
          ReactDOM.flushSync(() => {
            root.render(React.createElement(DynamicComponent, props));
          });
          hide(placeholderSelector);
          setErrorMessage(null);
        }
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Unknown dynamic component error.';
        console.error('Error rendering dynamic component:', error);
        hide(placeholderSelector);
        reveal(fallbackSelector);
        setErrorMessage(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [componentCode, props, allowedModules, fallbackSelector, placeholderSelector]);

  return (
    <div className="dynamic-component-renderer">
      <div ref={containerRef}></div>
      {errorMessage && (
        <div className="dynamic-component-renderer__error" data-dynamic-component-error="true" style={{ display: 'none' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default DynamicComponentRenderer;
